import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { FormValidationException } from '../utils/error/FormValidationException.ts';
import type { RedirectType } from '../types';

export type BlockingType = 'dirtyForm' | 'validForm';

export type BlockingCondition = {
  id: string;
  isBlocking: () => boolean;
  unblock?: (props?: UnblockProps) => Promise<boolean | void>;
  type: BlockingType;
};

export type PendingAction = {
  action: () => void;
  context?: PendingActionContext;
};

export type PendingActionContext = {
  onClose?: () => void;
  onCancel?: () => void;
  unblockProps?: UnblockProps;
};

export type UnblockProps = {
  redirectType?: RedirectType;
};

export type ActionBlockerApi = {
  registerBlockingCondition: (type: BlockingType, condition: Omit<BlockingCondition, 'type'>) => void;
  unregisterBlockingCondition: (id: string) => void;
  executeAction: (type: BlockingType, action: () => void, context?: PendingAction['context']) => Promise<boolean>;
  executeDelayed: (type: BlockingType, action: () => void, context?: PendingAction['context']) => Promise<boolean>;
  hasBlockingConditions: (type?: BlockingType) => boolean;
  getBlockingConditions: (type?: BlockingType) => BlockingCondition[];
  isBlocking: (type: BlockingType) => boolean;
  pendingAction: PendingAction | null;
  getWarningModalProps: (type: BlockingType) => {
    isOpen: boolean;
    onClose: () => void;
    onCancel: () => void;
  };
  getSaveModalProps: (type: BlockingType) => {
    isOpen: boolean;
    onSubmit: () => Promise<void>;
    onClose: () => void;
    onCancel: () => void;
  };
};

const ActionBlockerContext = createContext<ActionBlockerApi | null>(null);

export function ActionBlockerProvider({ children }: { children: ReactNode }) {
  const blockingConditionsRef = useRef<Map<string, BlockingCondition>>(new Map());
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const blockingStatesRef = useRef<Record<BlockingType, boolean>>({
    dirtyForm: false,
    validForm: false,
  });

  const setIsBlocking = useCallback((type: BlockingType, value: boolean) => {
    blockingStatesRef.current[type] = value;
  }, []);

  const isBlocking = useCallback((type: BlockingType) => blockingStatesRef.current[type], []);

  const registerBlockingCondition = useCallback((type: BlockingType, condition: Omit<BlockingCondition, 'type'>) => {
    blockingConditionsRef.current.set(condition.id, { ...condition, type });
  }, []);

  const unregisterBlockingCondition = useCallback((id: string) => {
    blockingConditionsRef.current.delete(id);
  }, []);

  const hasBlockingConditions = useCallback((type?: BlockingType) => {
    return Array.from(blockingConditionsRef.current.values()).some((condition) => {
      return type === undefined ? condition.isBlocking() : condition.type === type && condition.isBlocking();
    });
  }, []);

  const getBlockingConditions = useCallback((type?: BlockingType) => {
    const all = Array.from(blockingConditionsRef.current.values());
    if (type === undefined) {
      return all.filter((c) => c.isBlocking());
    }
    return all.filter((c) => c.type === type && c.isBlocking());
  }, []);

  const executeAction = useCallback(
    async (type: BlockingType, action: () => void, context?: PendingAction['context']): Promise<boolean> => {
      if (!hasBlockingConditions(type)) {
        action();
        setIsBlocking(type, false);
        return true;
      }

      setPendingAction({ action, context });
      setIsBlocking(type, true);
      return false;
    },
    [hasBlockingConditions, setIsBlocking]
  );

  const executeDelayed = useCallback(
    async (type: BlockingType, action: () => void, context?: PendingAction['context']): Promise<boolean> => {
      await new Promise((resolve) => setTimeout(resolve, 50));
      return executeAction(type, action, context);
    },
    [executeAction]
  );

  const handleSubmit = useCallback(
    async (type: BlockingType) => {
      if (!pendingAction) return;
      const blockingConditions = getBlockingConditions(type);

      let hasAlreadyNavigated = false;

      try {
        for (const condition of blockingConditions) {
          if (condition.unblock && condition.isBlocking()) {
            const result = await condition.unblock();
            if (result === true) {
              hasAlreadyNavigated = true;
            }
          }
        }
      } catch (error) {
        setPendingAction(null);
        setIsBlocking(type, false);
        if (error instanceof FormValidationException) return;
        // eslint-disable-next-line no-console
        console.error(error);
        return;
      }

      // Unblocking (e.g. saving) may already navigate elsewhere (e.g. via a server redirectAction).
      // In that case the originally blocked navigation is stale and must not override it.
      if (!hasAlreadyNavigated) {
        pendingAction.action();
      }
      setPendingAction(null);
      setIsBlocking(type, false);
    },
    [pendingAction, getBlockingConditions, setIsBlocking]
  );

  const handleClose = useCallback(
    (type: BlockingType) => {
      if (!pendingAction) return;

      pendingAction.context?.onClose?.();
      pendingAction.action();
      setPendingAction(null);
      setIsBlocking(type, false);
    },
    [pendingAction, setIsBlocking]
  );

  const handleCancel = useCallback(
    (type: BlockingType) => {
      pendingAction?.context?.onCancel?.();
      setPendingAction(null);
      setIsBlocking(type, false);
    },
    [pendingAction?.context, setIsBlocking]
  );

  const getWarningModalProps = useCallback(
    (type: BlockingType) => {
      return {
        isOpen: isBlocking(type),
        onClose: () => handleClose(type),
        onCancel: () => handleCancel(type),
      };
    },
    [handleCancel, isBlocking, handleClose]
  );

  const getSaveModalProps = useCallback(
    (type: BlockingType) => {
      return {
        isOpen: isBlocking(type),
        onSubmit: () => handleSubmit(type),
        onClose: () => handleClose(type),
        onCancel: () => handleCancel(type),
      };
    },
    [handleCancel, handleClose, handleSubmit, isBlocking]
  );

  const api = useMemo<ActionBlockerApi>(
    () => ({
      registerBlockingCondition,
      unregisterBlockingCondition,
      executeAction,
      executeDelayed,
      hasBlockingConditions,
      getBlockingConditions,
      isBlocking,
      pendingAction,
      getSaveModalProps,
      getWarningModalProps,
    }),
    [
      registerBlockingCondition,
      unregisterBlockingCondition,
      executeAction,
      executeDelayed,
      hasBlockingConditions,
      getBlockingConditions,
      isBlocking,
      pendingAction,
      getSaveModalProps,
      getWarningModalProps,
    ]
  );

  return <ActionBlockerContext.Provider value={api}>{children}</ActionBlockerContext.Provider>;
}

export function useActionBlocker() {
  const context = useContext(ActionBlockerContext);
  if (!context) {
    throw new Error('useActionBlocker must be used within ActionBlockerProvider');
  }
  return context;
}
